/**
 * Time Value Object
 *
 * Consolidates time parsing and formatting logic that was previously
 * duplicated in ServiceFormSidebar.vue (parseTime) and other components.
 *
 * Handles various time formats:
 * - HH:MM (24-hour format)
 * - H:MM (single digit hour)
 * - "9 AM", "5 PM" (12-hour format with AM/PM)
 * - "12:15 AM", "1:30 PM" (12-hour format with minutes)
 */
// =============================================================================
// CONSTANTS
// =============================================================================
/** Default time when parsing fails */
const DEFAULT_TIME = '00:00';
/** 24-hour time format regex */
const TIME_24H_REGEX = /^(\d{1,2}):(\d{2})$/;
/** 12-hour time format regex (e.g., "9 AM", "5 PM") */
const TIME_12H_REGEX = /^(\d{1,2})\s*(AM|PM)$/i;
/** 12-hour time format WITH minutes regex (e.g., "12:15 AM", "1:30 PM") */
const TIME_12H_WITH_MINUTES_REGEX = /^(\d{1,2}):(\d{2})\s*(AM|PM)$/i;
// =============================================================================
// TIME VALUE OBJECT
// =============================================================================
export const Time = {
    // ---------------------------------------------------------------------------
    // PARSING
    // ---------------------------------------------------------------------------
    /**
     * Parse any time string to HH:MM format
     * Handles: HH:MM, H:MM, "9 AM", "5 PM"
     *
     * @example Time.parse('9 AM') // '09:00'
     * @example Time.parse('5 PM') // '17:00'
     * @example Time.parse('14:30') // '14:30'
     * @example Time.parse('9:05') // '09:05'
     */
    parse(timeString) {
        if (!timeString)
            return DEFAULT_TIME;
        const time = timeString.trim().toUpperCase();
        // Try 24-hour format first (HH:MM or H:MM)
        const match24h = time.match(TIME_24H_REGEX);
        if (match24h) {
            const hours = parseInt(match24h[1], 10);
            const minutes = parseInt(match24h[2], 10);
            if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
                return this.format(hours, minutes);
            }
        }
        // Try 12-hour format WITH minutes (e.g., "12:15 AM", "1:30 PM")
        const match12hWithMin = time.match(TIME_12H_WITH_MINUTES_REGEX);
        if (match12hWithMin) {
            let hours = parseInt(match12hWithMin[1], 10);
            const minutes = parseInt(match12hWithMin[2], 10);
            const period = match12hWithMin[3];
            // Convert to 24-hour
            if (period === 'PM' && hours !== 12) {
                hours += 12;
            }
            else if (period === 'AM' && hours === 12) {
                hours = 0;
            }
            if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
                return this.format(hours, minutes);
            }
        }
        // Try 12-hour format WITHOUT minutes (e.g., "9 AM", "5 PM")
        const match12h = time.match(TIME_12H_REGEX);
        if (match12h) {
            let hours = parseInt(match12h[1], 10);
            const period = match12h[2];
            // Convert to 24-hour
            if (period === 'PM' && hours !== 12) {
                hours += 12;
            }
            else if (period === 'AM' && hours === 12) {
                hours = 0;
            }
            if (hours >= 0 && hours <= 23) {
                return this.format(hours, 0);
            }
        }
        return DEFAULT_TIME;
    },
    /**
     * Parse time string to components
     * @example Time.parseToComponents('14:30') // { hours: 14, minutes: 30 }
     */
    parseToComponents(timeString) {
        const parsed = this.parse(timeString);
        const [hours, minutes] = parsed.split(':').map(Number);
        return { hours, minutes };
    },
    // ---------------------------------------------------------------------------
    // FORMATTING
    // ---------------------------------------------------------------------------
    /**
     * Format hours and minutes to HH:MM
     * @example Time.format(9, 5) // '09:05'
     * @example Time.format(14, 30) // '14:30'
     */
    format(hours, minutes) {
        const h = Math.max(0, Math.min(23, hours));
        const m = Math.max(0, Math.min(59, minutes));
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
    },
    /**
     * Format time to 12-hour format with AM/PM
     * @example Time.to12Hour('14:30') // '2:30 PM'
     * @example Time.to12Hour('09:00') // '9:00 AM'
     */
    to12Hour(timeString) {
        const { hours, minutes } = this.parseToComponents(timeString);
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours % 12 || 12;
        return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    },
    /**
     * Format time for display (Spanish locale)
     * @example Time.formatDisplay('14:30') // '14:30 hrs'
     */
    formatDisplay(timeString) {
        return `${this.parse(timeString)} hrs`;
    },
    // ---------------------------------------------------------------------------
    // COMPARISON
    // ---------------------------------------------------------------------------
    /**
     * Check if time1 is before time2
     * @example Time.isBefore('09:00', '17:00') // true
     * @example Time.isBefore('17:00', '09:00') // false
     */
    isBefore(time1, time2) {
        const t1 = this.parseToComponents(time1);
        const t2 = this.parseToComponents(time2);
        if (t1.hours !== t2.hours) {
            return t1.hours < t2.hours;
        }
        return t1.minutes < t2.minutes;
    },
    /**
     * Check if time1 is after time2
     */
    isAfter(time1, time2) {
        return this.isBefore(time2, time1);
    },
    /**
     * Check if time1 equals time2
     */
    equals(time1, time2) {
        return this.parse(time1) === this.parse(time2);
    },
    /**
     * Check if end time is after start time (for time range validation)
     * This is the validation used in AvailabilityConfigurationModal
     */
    isValidRange(startTime, endTime) {
        return this.isBefore(startTime, endTime);
    },
    // ---------------------------------------------------------------------------
    // CALCULATION
    // ---------------------------------------------------------------------------
    /**
     * Get duration in minutes between two times
     * @example Time.getDurationMinutes('09:00', '17:30') // 510
     */
    getDurationMinutes(startTime, endTime) {
        const start = this.parseToComponents(startTime);
        const end = this.parseToComponents(endTime);
        const startMinutes = start.hours * 60 + start.minutes;
        const endMinutes = end.hours * 60 + end.minutes;
        return endMinutes - startMinutes;
    },
    /**
     * Get duration in hours (with decimals)
     * @example Time.getDurationHours('09:00', '17:30') // 8.5
     */
    getDurationHours(startTime, endTime) {
        return this.getDurationMinutes(startTime, endTime) / 60;
    },
    /**
     * Format duration for display
     * @example Time.formatDuration('09:00', '17:30') // '8h 30min'
     */
    formatDuration(startTime, endTime) {
        const minutes = this.getDurationMinutes(startTime, endTime);
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        if (hours === 0) {
            return `${remainingMinutes}min`;
        }
        if (remainingMinutes === 0) {
            return `${hours}h`;
        }
        return `${hours}h ${remainingMinutes}min`;
    },
    // ---------------------------------------------------------------------------
    // VALIDATION
    // ---------------------------------------------------------------------------
    /**
     * Validate a time string
     */
    validate(timeString) {
        if (!timeString || !timeString.trim()) {
            return { valid: false, error: 'La hora es requerida' };
        }
        const parsed = this.parse(timeString);
        if (parsed === DEFAULT_TIME && timeString.trim() !== DEFAULT_TIME) {
            return { valid: false, error: 'Formato de hora inválido' };
        }
        return { valid: true };
    },
    /**
     * Validate a time range
     */
    validateRange(startTime, endTime) {
        const startValidation = this.validate(startTime);
        if (!startValidation.valid) {
            return { valid: false, error: `Hora de inicio: ${startValidation.error}` };
        }
        const endValidation = this.validate(endTime);
        if (!endValidation.valid) {
            return { valid: false, error: `Hora de fin: ${endValidation.error}` };
        }
        if (!this.isValidRange(startTime, endTime)) {
            return {
                valid: false,
                error: 'La hora de finalización debe ser posterior a la hora de inicio',
            };
        }
        return { valid: true };
    },
    // ---------------------------------------------------------------------------
    // UTILITIES
    // ---------------------------------------------------------------------------
    /**
     * Get current time in HH:MM format
     */
    now() {
        const now = new Date();
        return this.format(now.getHours(), now.getMinutes());
    },
    /**
     * Convert minutes since midnight to time string
     * @example Time.fromMinutes(510) // '08:30'
     */
    fromMinutes(minutes) {
        const hours = Math.floor(minutes / 60) % 24;
        const mins = minutes % 60;
        return this.format(hours, mins);
    },
    /**
     * Convert time string to minutes since midnight
     * @example Time.toMinutes('08:30') // 510
     */
    toMinutes(timeString) {
        const { hours, minutes } = this.parseToComponents(timeString);
        return hours * 60 + minutes;
    },
};
// =============================================================================
// DEFAULT EXPORT
// =============================================================================
export default Time;
